/**
 * The Fabs part of the main menu: where you can stand, and the library of
 * floors you designed.
 *
 * TWO KINDS OF ROW, DELIBERATELY. Fab A and Fab B are destinations - the
 * same twenty-five tools in two halls - and the custom floor you are standing
 * in, if any, is a destination too; those sit at the top with a tick beside
 * the one you are in, as they always have. The library is a submenu of
 * verbs, the way a document has them: Open, Save, Save as, and Design. A
 * fab that is saved is not a place until it is opened, so it is not listed
 * among the places.
 *
 * WHOSE FABS. The library is the account's (fab/fabLibrary.ts), so the rows
 * under Open are the fabs THIS person saved, on any machine - never the ones
 * some earlier student on this machine left behind. Signed out, the verbs
 * explain themselves and do nothing; the account button in the corner is
 * where signing in lives, and this menu does not duplicate it.
 *
 * Save writes the floor you are standing in. Save as makes a copy under a
 * new name and stands you in the copy, the way an editor does; a second
 * Save then goes to the copy.
 */

import { useEffect, useState } from 'react'
import { currentPlan, hallIdFor, setHall, type HallLayout } from '../../fab/hall'
import { newPlanId, savePlan, setActivePlanId, type FloorPlan } from '../../fab/floorPlan'
import { fabLibrary, NAME_MAX } from '../../fab/fabLibrary'
import { auth } from '../../auth/Auth'
import { carryPlaceTo } from '../../fab/place'
import { dialogStore } from '../ui/dialogStore'
import { confirmDialog } from '../ui/confirm'
import { showLoadingThen } from './loading'
import { t, tOr } from '../../i18n/i18n'
import { useAuthTick, useHall, usePlans } from '../state/hooks'
import { useTick } from '../state/useObservable'

type Leaf = 'open' | 'saveAs'

function useLibrary(): number {
  return useTick((fn) => fabLibrary.subscribe(fn))
}

const noteFor = (p: FloorPlan): string =>
  t('menu.CustomFabNote', { n: p.tools.length, hall: tOr(`author.hall.${p.hall}`, p.hall) })

/** Stand in a floor: into the local working copy, then the hall is rebuilt. */
function standIn(plan: FloorPlan, onPick: () => void): void {
  savePlan(plan)
  setActivePlanId(plan.id)
  const id = hallIdFor(plan.id)
  carryPlaceTo(id)
  onPick()
  showLoadingThen(id, () => setHall(id))
}

export function FabsMenu({ onPick }: { onPick: () => void }) {
  const active = useHall()
  usePlans()
  useAuthTick()
  useLibrary()
  const [open, setOpen] = useState(false)
  const [leaf, setLeaf] = useState<Leaf | null>(null)
  const [saveAsName, setSaveAsName] = useState('')
  const [flash, setFlash] = useState<string | null>(null)

  const signedIn = Boolean(auth.account)
  const standing = currentPlan()

  // The library is fetched when its list is opened, not when the menu is:
  // a student who only wants Fab B should not cost a round trip.
  useEffect(() => {
    if (leaf === 'open' && signedIn && fabLibrary.phase === 'idle') void fabLibrary.refresh()
  }, [leaf, signedIn])

  useEffect(() => {
    if (!flash) return
    const id = window.setTimeout(() => setFlash(null), 2500)
    return () => window.clearTimeout(id)
  }, [flash])

  const places: Array<{ value: HallLayout; label: string; note: string }> = [
    { value: 'bayChase', label: tOr('menu.HallBayChase', 'Fab A'), note: tOr('menu.HallBayChaseNote', 'bay-and-chase layout — tool faces in a wall, the chase behind') },
    { value: 'ballroom', label: tOr('menu.HallBallroom', 'Fab B'), note: tOr('menu.HallBallroomNote', 'ballroom layout — open floor, every mainframe in view') },
  ]
  if (standing) places.push({ value: hallIdFor(standing.id), label: standing.name, note: noteFor(standing) })

  const save = async (): Promise<void> => {
    if (!standing || !signedIn || fabLibrary.busy) return
    try {
      await fabLibrary.save(standing)
      setFlash(t('menu.FabSaved', { name: standing.name }))
    } catch {
      /* the library's own error line says what happened */
    }
  }

  const saveAs = async (): Promise<void> => {
    if (!standing || !signedIn || fabLibrary.busy) return
    const name = saveAsName.trim().slice(0, NAME_MAX)
    if (!name) return
    const copy: FloorPlan = { ...standing, id: newPlanId(), name, savedAt: Date.now() }
    try {
      await fabLibrary.save(copy)
    } catch {
      return
    }
    setLeaf(null)
    setOpen(false)
    standIn(copy, onPick)
  }

  const remove = async (p: FloorPlan): Promise<void> => {
    const yes = await confirmDialog({
      title: tOr('menu.DeleteFab', 'Delete from your fabs'),
      body: t('menu.DeleteFabConfirm', { name: p.name }),
      okText: tOr('author.Delete', 'Delete'),
      cancelText: tOr('author.Cancel', 'Keep it'),
    })
    if (!yes) return
    try {
      await fabLibrary.remove(p.id)
    } catch {
      /* reported by the library */
    }
  }

  const verb = (id: Leaf | 'save' | 'design', label: string, note: string, disabled: boolean, onClick: () => void) => (
    <button
      className={'mainmenu-item' + (leaf === id ? ' on' : '')}
      role="menuitem"
      title={note}
      disabled={disabled}
      onClick={onClick}
    >
      <span>{label}</span>
      <span className="mainmenu-chevron">{id === 'open' || id === 'saveAs' ? '›' : ''}</span>
    </button>
  )

  const whenOf = (ms: number): string => (ms ? new Date(ms).toLocaleDateString() : '')

  return (
    <>
      {places.map((r) => {
        const on = r.value === active
        return (
          <button
            key={r.value}
            className={'mainmenu-item' + (on ? ' on' : '')}
            role="menuitemradio"
            aria-checked={on}
            title={r.note}
            onClick={() => {
              if (on) return
              // Where you are standing goes with you the first time; after
              // that each fab remembers its own. The menu goes away, the
              // screen goes up and is painted, and only then does the
              // rebuild block the thread.
              carryPlaceTo(r.value)
              onPick()
              showLoadingThen(r.value, () => setHall(r.value))
            }}
          >
            <span>{r.label}</span>
            <span className="mainmenu-chevron">{on ? '✓' : ''}</span>
          </button>
        )
      })}

      <div className="mainmenu-row">
        <button
          className={'mainmenu-item' + (open ? ' on' : '')}
          role="menuitem"
          aria-haspopup="menu"
          aria-expanded={open}
          title={tOr('menu.FabsNote', 'Your own floors: open one, save this one, or design a new one')}
          onClick={() => {
            setOpen(!open)
            setLeaf(null)
          }}
        >
          <span>{tOr('menu.Fabs', 'My fabs')}</span>
          <span className="mainmenu-chevron">›</span>
        </button>
        {open && (
          <div className="mainmenu-submenu" role="menu">
            {!signedIn && (
              <div className="mainmenu-note">{t('menu.LibrarySignedOut')}</div>
            )}

            {verb('open', tOr('menu.OpenFab', 'Open…'), tOr('menu.OpenFabNote', 'the fabs saved to your account'), !signedIn, () =>
              setLeaf(leaf === 'open' ? null : 'open'),
            )}
            {leaf === 'open' && (
              <div className="mainmenu-leaf">
                {fabLibrary.phase === 'loading' && <div className="mainmenu-note">{t('menu.LibraryLoading')}</div>}
                {fabLibrary.phase === 'error' && <div className="mainmenu-note">{fabLibrary.error}</div>}
                {fabLibrary.phase === 'ready' && fabLibrary.fabs.length === 0 && (
                  <div className="mainmenu-note">{t('menu.NoSavedFabs')}</div>
                )}
                {fabLibrary.phase === 'ready' && fabLibrary.fabs.length > 0 && (
                  <div className="mainmenu-options">
                    {fabLibrary.fabs.map((p) => {
                      const here = hallIdFor(p.id) === active
                      return (
                        <div key={p.id} className="mainmenu-inline">
                          <button
                            className={'mainmenu-option' + (here ? ' on' : '')}
                            role="menuitem"
                            title={t('menu.SavedOn', { when: whenOf(p.savedAt) })}
                            onClick={() => {
                              if (here) return
                              setLeaf(null)
                              setOpen(false)
                              standIn(p, onPick)
                            }}
                          >
                            <span className="mainmenu-optlabel">{p.name}</span>
                            <span className="mainmenu-optnote">{noteFor(p)}</span>
                          </button>
                          <button
                            className="mainmenu-x"
                            title={tOr('menu.DeleteFab', 'Delete from your fabs')}
                            disabled={fabLibrary.busy}
                            onClick={() => void remove(p)}
                          >
                            ×
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {verb('save', tOr('menu.SaveFab', 'Save'), standing ? tOr('menu.SaveFabNote', 'keep the floor you are standing in, under its name') : tOr('menu.SaveNothing', 'Fab A and Fab B are built in; design a fab to have one to save'), !signedIn || !standing || fabLibrary.busy, () => void save())}
            {flash && <div className="mainmenu-note">{flash}</div>}

            {verb('saveAs', tOr('menu.SaveFabAs', 'Save as…'), tOr('menu.SaveFabAsNote', 'a copy under a new name, and you stand in the copy'), !signedIn || !standing, () => {
              setSaveAsName(standing?.name ?? '')
              setLeaf(leaf === 'saveAs' ? null : 'saveAs')
            })}
            {leaf === 'saveAs' && standing && (
              <div className="mainmenu-leaf">
                <input
                  className="mainmenu-field"
                  value={saveAsName}
                  maxLength={NAME_MAX}
                  placeholder={tOr('menu.SaveAsName', 'Name for the copy')}
                  onChange={(e) => setSaveAsName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void saveAs()
                    e.stopPropagation()
                  }}
                />
                <button className="btn primary" disabled={!saveAsName.trim() || fabLibrary.busy} onClick={() => void saveAs()}>
                  {tOr('menu.SaveAsGo', 'Save the copy')}
                </button>
                {fabLibrary.phase === 'error' && fabLibrary.error && (
                  <div className="mainmenu-note">{fabLibrary.error}</div>
                )}
              </div>
            )}

            {verb('design', tOr('menu.DesignFab', 'Design a fab'), tOr('menu.DesignFabNote', 'build your own floor from the twenty-five stations'), false, () => {
              onPick()
              dialogStore.open({ kind: 'author' })
            })}
          </div>
        )}
      </div>
    </>
  )
}
